import { Link } from "react-router-dom";
import type { CaseItem } from "@/mocks/cases";

interface CaseCardProps {
  item: CaseItem;
  variant?: "default" | "wide" | "compact";
  index?: number;
}

export default function CaseCard({ item, variant = "default", index }: CaseCardProps) {
  const to = `/cases/${item.id}`;

  if (variant === "compact") {
    return (
      <Link
        to={to}
        className="group flex gap-4 py-5 border-b border-background-200 hover:border-primary-300 transition-colors"
      >
        <span className="shrink-0 w-14 h-14 flex items-center justify-center rounded-md bg-background-100 text-foreground-400 font-heading text-lg font-bold group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
          {typeof index === "number" ? String(index + 1).padStart(2, "0") : <i className="ri-article-line" aria-hidden="true"></i>}
        </span>
        <div className="min-w-0">
          <p className="text-xs text-primary-600 font-medium mb-1">{item.categoryLabel}</p>
          <h3 className="font-heading text-base font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {item.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-500">
            {item.author} ｜ {item.date} ｜ {item.readTime}
          </p>
        </div>
      </Link>
    );
  }

  const isWide = variant === "wide";

  return (
    <article
      className={`group bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-primary-300 transition-colors ${
        isWide ? "md:grid md:grid-cols-2" : "flex flex-col"
      }`}
    >
      <Link
        to={to}
        className={`block relative overflow-hidden bg-secondary-900 ${
          isWide ? "aspect-[16/10] md:aspect-auto md:h-full md:min-h-[260px]" : "aspect-[16/10]"
        }`}
        aria-label={item.title}
      >
        <img
          src={item.cover}
          alt={item.coverAlt}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-background-950/75 text-background-50 text-xs font-medium backdrop-blur-sm">
          {item.categoryLabel}
        </span>
      </Link>
      <div className={`flex flex-col p-5 md:p-6 ${isWide ? "justify-center" : "flex-1"}`}>
        <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2.5">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
            {item.heat.toLocaleString()} 阅读
          </span>
          <span>{item.readTime}</span>
        </div>
        <h3
          className={`font-heading font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors ${
            isWide ? "text-xl md:text-2xl" : "text-lg"
          }`}
        >
          <Link to={to}>{item.title}</Link>
        </h3>
        <p className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-3">
          {item.summary}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {item.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 rounded-md bg-secondary-50 text-secondary-900 text-xs"
            >
              #{tag}
            </span>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
          <span>{item.author}</span>
          <span className="flex items-center gap-1 text-primary-600 font-medium">
            阅读档案
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" aria-hidden="true"></i>
          </span>
        </div>
      </div>
    </article>
  );
}